import { useState, useCallback } from "react"

/**
 * Hook to manage undo/redo history for canvas elements
 * @param {Array} elements - Current elements state
 * @returns {Object} { history, redoStack, addToHistory, undo, redo }
 */
export function useUndoRedo(elements) {
  const [history, setHistory] = useState([])
  const [redoStack, setRedoStack] = useState([])

  const addToHistory = useCallback(
    newElements => {
      // Only add to history if elements actually changed
      if (JSON.stringify(elements) !== JSON.stringify(newElements)) {
        setHistory(prevHistory => [...prevHistory, [...elements]]) // Push a copy of current state
        setRedoStack([]) // Clear redo stack on new actions
        return newElements // Return the new elements to be set by caller
      }
      return elements
    },
    [elements],
  )

  const undo = useCallback(() => {
    if (history.length > 0) {
      const previousState = history[history.length - 1]
      setRedoStack(prevRedo => [elements, ...prevRedo])
      setHistory(history.slice(0, -1))
      return previousState
    }
    return null
  }, [history, elements])

  const redo = useCallback(() => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0]
      setRedoStack(redoStack.slice(1))
      setHistory(prevHistory => [...prevHistory, elements])
      return nextState
    }
    return null
  }, [redoStack, elements])

  return {
    history,
    redoStack,
    addToHistory,
    undo,
    redo,
  }
}
